// Search-intent landing pages: one page per query people actually type ("football manager
// online free", "games like football manager", ...). Plain data (HTML strings) so
// scripts/prerender.mjs can bake the full text into static HTML, like the blog.
// related: paths of sibling pages linked at the bottom (internal linking).

const landingPages = [
  {
    path: '/football-manager-online-free',
    lang: 'en',
    title: 'Football Manager Games Online Free – No Download | My Football Game',
    description: 'Play a free online football manager game in your browser, no download. Build a club, pick tactics, trade players, play PvP against friends and win promotion.',
    eyebrow: 'Free to play',
    h1: 'FREE ONLINE ',
    h1Accent: 'FOOTBALL MANAGER GAME',
    subtitle: 'A full football manager game that runs in your browser. Free to play, no download, and your first match is about a minute away.',
    breadcrumb: 'Free Football Manager',
    sections: [
      {
        heading: 'A real football manager, free, in your browser',
        html: `<p>My Football Game is a free online football manager. You take over a club, choose the starting eleven, pick a formation and play a full league season against rival clubs. Every result moves you up or down the table, and the board watches every one of them.</p>
<p>There is nothing to install. It runs in Chrome, Safari, Edge and Firefox on desktop, tablet and phone, and your club is saved to your account so you can switch devices mid-season.</p>`,
      },
      {
        heading: 'What you actually do as manager',
        html: `<ul>
<li><strong>Pick your eleven.</strong> 18 formations (4-3-3, 4-4-2, 3-5-2, 4-2-3-1 and more) across 26 positions. Players are strongest in their natural position.</li>
<li><strong>Set your tactics.</strong> Mentality, pressing and set-piece takers all feed the match engine, so tactics change results instead of just decorating them.</li>
<li><strong>Watch the match.</strong> Matches play out minute by minute with goals, cards, injuries, substitutions, possession, shots and xG.</li>
<li><strong>Trade on the transfer market.</strong> Buy and sell players with other managers. Selling a star at the right time can fund a whole new spine.</li>
<li><strong>Train your squad.</strong> Young players grow faster; veterans give you consistency.</li>
</ul>`,
      },
      {
        heading: 'Full seasons in minutes, not months',
        html: `<p>Each season is an 18-matchday league against nine rival clubs. Finish high and you are promoted; finish low and you go down. There are five divisions: Amateur, Regional, National, Championship and Elite.</p>
<p>The board sets a target every season. Hit it and it backs you. Lose too often and it gives you an ultimatum: win two of the next three or lose the season prize. It is the pressure of a real manager's job, packed into a session you can finish on a lunch break.</p>`,
      },
      {
        heading: 'Is it really free?',
        html: `<p>Yes. Every feature can be earned by playing. The game has two currencies: coins, which you earn from matches and daily challenges, and gold, which can also be bought. Gold is a shortcut, never a wall: there is no feature you can only unlock by paying.</p>`,
      },
      {
        heading: 'Play against your friends',
        html: `<p>Send a challenge link on WhatsApp or anywhere else. Your friend plays against your club straight away, and every match between you counts toward your head-to-head record. You can also create a private league and run a full season with your group.</p>`,
      },
    ],
    faqs: [
      { q: 'Is My Football Game free?', a: 'Yes. It is free to play and every feature can be earned through gameplay. Gold can be bought optionally, but nothing is locked behind a payment.' },
      { q: 'Do I need to download anything?', a: 'No. It runs in your web browser on desktop, tablet and mobile. Create a free club and play straight away.' },
      { q: 'How long does a season take?', a: 'A season is 18 matchdays and you can play it at your own pace. Many managers finish a season in a few sessions.' },
      { q: 'Can I play without signing in?', a: 'You create a free club with email or Google in about 30 seconds, and it is saved to your account. Friends you challenge with a link can play your club without signing up.' },
      { q: 'Can I play with friends?', a: 'Yes. Send a challenge link, add friends in-game, or create a private league for your group.' },
      { q: 'Can I see how my players are performing?', a: 'Yes. After every match you get individual player ratings plus team stats such as possession, shots, xG and pass accuracy, so you can see who is playing well and who to drop.' },
    ],
    related: ['/football-manager-no-download', '/football-manager-pvp', '/create-your-own-football-club', '/games-like-football-manager', '/soccer-manager-game', '/blog/football-manager-online-free-2026'],
  },
  {
    path: '/football-manager-no-download',
    lang: 'en',
    title: 'Football Manager With No Download – Play in Browser | My Football Game',
    description: 'A browser-based football manager with no download and no install. Works on any computer, Chromebook or phone, including school and work laptops. Free to play.',
    eyebrow: 'Browser based',
    h1: 'FOOTBALL MANAGER, ',
    h1Accent: 'NO DOWNLOAD',
    subtitle: 'No installer, no launcher, no 10 GB update. Open the page, create your club and play.',
    breadcrumb: 'No Download',
    sections: [
      {
        heading: 'Why a browser football manager?',
        html: `<p>Most management games ask for a big download, a decent graphics card and a store account before you can pick a single player. My Football Game skips all of that. It is a web app: the page loads in seconds and the match engine runs on our servers, so an old laptop plays exactly the same as a gaming PC.</p>`,
      },
      {
        heading: 'Works on the computer you already have',
        html: `<ul>
<li><strong>Windows, Mac and Linux</strong> in any modern browser.</li>
<li><strong>Chromebooks</strong>, where most downloadable manager games do not run at all.</li>
<li><strong>Locked-down laptops</strong> where you cannot install software.</li>
<li><strong>Phones and tablets</strong>, with a layout built for touch.</li>
</ul>
<p>Your club lives in your account, not on one machine. Start a season on your PC and finish it on your phone.</p>`,
      },
      {
        heading: 'Small download, full depth',
        html: `<p>Browser does not mean shallow. You still get 18 formations, 26 positions, mentality and pressing settings, set-piece takers, a transfer market with other managers, player training, five divisions with promotion and relegation, and a board that judges every result.</p>
<p>Matches are simulated minute by minute with full stats (possession, shots, xG), so you can see why you won or lost and change the plan for next week.</p>`,
      },
      {
        heading: 'Add it to your home screen',
        html: `<p>On a phone, use your browser's "Add to Home Screen" option. The game opens full screen like an app, still with nothing to install from a store.</p>`,
      },
    ],
    faqs: [
      { q: 'Does it work on a Chromebook?', a: 'Yes. My Football Game runs in Chrome, so it works on any Chromebook with no install.' },
      { q: 'Do I need a good computer?', a: 'No. Matches are simulated on our servers, so any device with a modern browser plays the same.' },
      { q: 'Is my progress saved?', a: 'Yes. Your club is saved to your account, so you can switch between computer and phone.' },
      { q: 'Is there a free download of My Football Game?', a: 'There is nothing to download. The full game is free in your browser on PC, Mac, Chromebook and phone. On a phone you can add it to your home screen so it opens like an app.' },
    ],
    related: ['/football-manager-online-free', '/football-manager-mobile', '/create-your-own-football-club', '/games-like-football-manager', '/how-to-play', '/blog/football-manager-no-download'],
  },
  {
    path: '/football-manager-mobile',
    lang: 'en',
    title: 'Football Manager on Mobile – Play on Your Phone, No App | My Football Game',
    description: 'Play a football manager game on your phone without an app store. Built for touch, quick matchdays, and your club syncs with desktop. Free on iPhone and Android browsers.',
    eyebrow: 'Mobile',
    h1: 'FOOTBALL MANAGER ',
    h1Accent: 'ON MOBILE',
    subtitle: 'Run your club from your phone. Quick matchdays, touch-first screens, and no app store required.',
    breadcrumb: 'Mobile',
    sections: [
      {
        heading: 'Made for a phone, not squeezed onto one',
        html: `<p>My Football Game was designed for short sessions. A matchday takes a couple of minutes: check the board, tweak the lineup, play the match, read the stats. The bottom navigation keeps Home, Club, Play, Compete and Profile under your thumb.</p>`,
      },
      {
        heading: 'iPhone and Android, straight from the browser',
        html: `<p>Open myfootballgame.com in Safari or Chrome and play. There is no store download and no permissions to approve. For an app-like experience, add it to your home screen and it opens full screen.</p>`,
      },
      {
        heading: 'Everything from the desktop version',
        html: `<ul>
<li>Lineups, 18 formations, mentality and set-piece takers.</li>
<li>Live match feed with goals, cards, substitutions and xG.</li>
<li>Transfer market, training and the full league table.</li>
<li>Challenge links you can send to friends on WhatsApp in one tap.</li>
</ul>
<p>Your club is the same account on every device, so a season started on the bus can be finished on a laptop.</p>`,
      },
    ],
    faqs: [
      { q: 'Is there an app?', a: 'You do not need one. The game runs in your phone browser, and you can add it to your home screen to open it like an app.' },
      { q: 'Does it work on iPhone?', a: 'Yes. It works in Safari and Chrome on iPhone, and in any modern browser on Android.' },
      { q: 'Does mobile progress sync with desktop?', a: 'Yes. Your club is saved to your account, so it is the same on every device.' },
    ],
    related: ['/football-manager-online-free', '/football-manager-no-download', '/features/play-with-friends', '/soccer-manager-game'],
  },
  {
    path: '/soccer-manager-game',
    lang: 'en',
    title: 'Soccer Manager Game Online – Free, Play Friends | My Football Game',
    description: 'A free online soccer management game: build your team, set tactics, trade players and play leagues against your friends. Runs in your browser, no download.',
    eyebrow: 'Soccer manager',
    h1: 'ONLINE SOCCER ',
    h1Accent: 'MANAGER GAME',
    subtitle: 'Call it football or soccer: you are the manager. Build the team, set the tactics, and answer to the board.',
    breadcrumb: 'Soccer Manager',
    sections: [
      {
        heading: 'Manage a soccer team from your browser',
        html: `<p>My Football Game is a soccer management simulation you play online for free. You do not control players on the field; you make the decisions a coach makes. Who starts, which formation, how aggressively to press, who takes the penalties, and which players to buy and sell.</p>`,
      },
      {
        heading: 'How a season works',
        html: `<ol>
<li><strong>Get your squad.</strong> Your club starts with a squad and a transfer budget.</li>
<li><strong>Set the lineup.</strong> Choose from 18 formations and put players in their natural positions.</li>
<li><strong>Play 18 matchdays.</strong> Face nine rival teams, with each match simulated minute by minute.</li>
<li><strong>Meet the board's target.</strong> Miss it too often and you get an ultimatum.</li>
<li><strong>Move up or down.</strong> Promotion and relegation across five divisions, from the Amateur League to the Elite League.</li>
</ol>`,
      },
      {
        heading: 'Stats that explain the result',
        html: `<p>After every match you see possession, shots, shots on target and expected goals (xG). If you lost while creating more chances, your tactics were right and luck was not. If you were outshot, it is time to rethink the midfield.</p>`,
      },
      {
        heading: 'Compete with real people',
        html: `<p>The transfer market is shared with other managers, the global leaderboard ranks coaches and players, and you can challenge friends with a single link. Private leagues let a group of friends play a whole season against each other.</p>`,
      },
    ],
    faqs: [
      { q: 'Is this a soccer game where I control the players?', a: 'No. It is a management game: you pick the team and tactics, and the match engine simulates the game based on your decisions.' },
      { q: 'Is it free?', a: 'Yes. It is free to play with no paywall. Everything can be earned in the game.' },
      { q: 'Can I play on my phone?', a: 'Yes. It runs in mobile browsers on iPhone and Android.' },
      { q: 'Can I play a soccer league against my friends?', a: 'Yes. Create a private league and invite your friends to play a full season against each other, or send a challenge link for a single match.' },
    ],
    related: ['/football-manager-online-free', '/football-manager-pvp', '/games-like-football-manager', '/features/tactics', '/features/seasons', '/blog/free-soccer-manager-game-online'],
  },
  {
    path: '/create-your-own-football-club',
    lang: 'en',
    title: 'Build Your Own Football Club From Scratch – Free | My Football Game',
    description: 'Build your own football (soccer) club from the ground up: name it, upload a crest, sign the squad and climb from the Amateur League to the Elite League. Free, in your browser.',
    eyebrow: 'Your club, from scratch',
    h1: 'CREATE YOUR OWN ',
    h1Accent: 'FOOTBALL CLUB',
    subtitle: 'Your name, your crest, your squad. Start in the Amateur league and build a club that climbs all the way to the top.',
    breadcrumb: 'Create Your Own Club',
    sections: [
      {
        heading: 'Name it, badge it, make it yours',
        html: `<p>In My Football Game you do not take over a famous club: you start your own. Choose the club name, upload your own crest image (or paste an image link), and your club gets its own colours, generated from the name, so it looks the same everywhere it appears: league tables, match screens and your friends' challenge links.</p>`,
      },
      {
        heading: 'Build the squad from the ground up',
        html: `<p>Your club starts with a squad and a transfer budget. From there, every player is your decision:</p>
<ul>
<li><strong>Sign and sell</strong> on a transfer market shared with other managers.</li>
<li><strong>Train</strong> individual attributes. Young players develop faster, veterans bring consistency.</li>
<li><strong>Pick the shape</strong> from 18 formations and 26 positions, and play people where they are strongest.</li>
<li><strong>Set the tactics</strong>: mentality, pressing and set-piece takers all change how matches go.</li>
</ul>`,
      },
      {
        heading: 'From the Amateur League to the Elite League',
        html: `<p>Every club starts in the Amateur division. Each season is 18 matchdays against nine rival clubs, with promotion and relegation across five divisions: Amateur, Regional, National, Championship and Elite.</p>
<p>The board sets a target each season. Meet it and the club grows; miss it too often and you get an ultimatum to win two of the next three. Building a club is not only about buying players, it is about surviving the seasons where things go wrong.</p>`,
      },
      {
        heading: 'Play your club against your friends',
        html: `<p>Send a challenge link and your friend plays against your club straight away. Every result counts in your head-to-head record. Want a full competition? Create a private league and play a whole season against your group, or climb the global leaderboard against managers everywhere.</p>`,
      },
    ],
    faqs: [
      { q: 'Can I create my own football club online for free?', a: 'Yes. In My Football Game you create your own club for free, choose its name, upload a crest and build the squad yourself.' },
      { q: 'Can I choose my own team name?', a: 'Yes. You name your club when you create it, and you can change the name and crest later in your team settings.' },
      { q: 'Can I upload my own club badge?', a: 'Yes. You can upload an image or paste an image link as your club crest. Club colours are generated automatically from the club name.' },
      { q: 'Can I play my club against friends?', a: 'Yes. Send a challenge link for a one-off match, or create a private league for a full season against your friends.' },
      { q: 'Are the clubs and players real?', a: 'No. Clubs and players are fictional, so every manager starts from the same place and builds their own club.' },
    ],
    related: ['/football-manager-online-free', '/football-manager-pvp', '/features/seasons', '/games-like-football-manager'],
  },
  {
    path: '/football-manager-pvp',
    lang: 'en',
    title: 'PvP Football Manager – Build a Squad, Beat Friends | My Football Game',
    description: 'A free PvP football manager: build your own squad, set tactics and play your friends in challenges or a private league. Matches are simulated live. No download.',
    eyebrow: 'Manager vs manager',
    h1: 'PVP FOOTBALL ',
    h1Accent: 'MANAGER',
    subtitle: 'Build your squad, pick your tactics, then put them up against your friends. Every result goes on your head-to-head record.',
    breadcrumb: 'PvP Football Manager',
    sections: [
      {
        heading: 'Your squad against theirs',
        html: `<p>My Football Game is a football manager where you play against real people, not only against the computer. You build your own squad, choose the formation and tactics, and the match engine simulates the game minute by minute from both managers' decisions. The better plan usually wins, and the stats (possession, shots, xG) show you why.</p>`,
      },
      {
        heading: 'Three ways to play your friends',
        html: `<ul>
<li><strong>Challenge link.</strong> Send a link on WhatsApp, Discord or anywhere else. Your friend plays against your club straight away, with no signup needed.</li>
<li><strong>Private league.</strong> Invite your group and play a full season against each other, with a league table and a prize at the end.</li>
<li><strong>Friends list.</strong> Add friends in-game and send direct challenges whenever you want a rematch.</li>
</ul>
<p>Every match between you counts toward your head-to-head record, so rivalries build up over the seasons.</p>`,
      },
      {
        heading: 'Build the squad before you face them',
        html: `<ul>
<li><strong>Transfers.</strong> Buy and sell on a transfer market shared with other managers.</li>
<li><strong>Training.</strong> Develop young players or rely on experienced ones.</li>
<li><strong>Tactics.</strong> 18 formations, 26 positions, mentality, pressing and set-piece takers.</li>
<li><strong>Seasons.</strong> 18 matchdays, five divisions, promotion, relegation and a demanding board.</li>
</ul>`,
      },
      {
        heading: 'Not only friends',
        html: `<p>No friends online? Find random opponents, take on the three daily challenges for bonus coins, or climb the global leaderboard against managers everywhere.</p>`,
      },
    ],
    faqs: [
      { q: 'Can I play a football manager game against my friends?', a: 'Yes. Send a challenge link for a single match or create a private league to play a full season against your friends.' },
      { q: 'Does my friend need an account?', a: 'No. A friend who opens your challenge link can play your club without signing up.' },
      { q: 'Is it real-time PvP?', a: 'Both managers set their squad and tactics, and the match engine simulates the game live, minute by minute, from those decisions. You do not control players on the pitch.' },
      { q: 'Is it free?', a: 'Yes. It is free to play in your browser, with no download and no paywall.' },
    ],
    related: ['/features/play-with-friends', '/create-your-own-football-club', '/football-manager-online-free', '/soccer-manager-game', '/features/leagues'],
  },
  {
    path: '/games-like-football-manager',
    lang: 'en',
    title: 'Games Like Football Manager – Free Browser Alternatives | My Football Game',
    description: 'Looking for games like Football Manager that are free and need no download? An honest look at browser and mobile football manager alternatives and who each one suits.',
    eyebrow: 'Alternatives',
    h1: 'GAMES LIKE ',
    h1Accent: 'FOOTBALL MANAGER',
    subtitle: 'Love management games but not the price, the download or the 40-hour seasons? Here are the alternatives, and where My Football Game fits.',
    breadcrumb: 'Games Like Football Manager',
    sections: [
      {
        heading: 'What people want when they search this',
        html: `<p>Football Manager is the deepest football management sim there is, but it is a paid game, a large download, and a season can take dozens of hours. Many players search for an alternative because they want something <strong>free</strong>, something that runs <strong>in a browser or on a phone</strong>, or something with <strong>shorter seasons</strong> and real opponents.</p>`,
      },
      {
        heading: 'The main types of alternatives',
        html: `<ul>
<li><strong>Deep offline sims.</strong> Paid PC games with huge databases. Best if you want maximum detail and have time.</li>
<li><strong>Mobile free-to-play managers</strong> such as Top Eleven. Polished app experiences built around daily sessions and live events.</li>
<li><strong>Classic browser managers</strong> such as Hattrick or Online Soccer Manager. Long-running communities, typically with slow, real-time schedules where a league match happens on a fixed day each week.</li>
<li><strong>Fast browser managers</strong> like My Football Game. Free, no download, and you play matches when you want instead of waiting for the next fixture.</li>
</ul>`,
      },
      {
        heading: 'Where My Football Game fits',
        html: `<p>My Football Game sits between a deep sim and a casual mobile game:</p>
<ul>
<li><strong>You play at your own pace.</strong> An 18-matchday season can be finished in a few sessions.</li>
<li><strong>Decisions matter.</strong> 18 formations, 26 positions, mentality, pressing and set-piece takers all feed the match engine.</li>
<li><strong>There is pressure.</strong> The board sets a target every season and issues ultimatums when you miss it.</li>
<li><strong>Your friends can play in seconds.</strong> A challenge link lets a friend play your club immediately.</li>
<li><strong>It is free and runs anywhere.</strong> Desktop, Chromebook, iPhone, Android.</li>
</ul>`,
      },
      {
        heading: 'Other free browser managers to try',
        html: `<p>If you want to compare, these are other football managers that describe themselves as free and browser-based. Check each site for current terms.</p>
<ul>
<li><strong>Hattrick:</strong> a long-running online manager with a large community and optional premium extras.</li>
<li><strong>ManagerZone:</strong> a free manager for browser and mobile.</li>
<li><strong>Sokker Manager:</strong> a browser manager with a 2D and 3D match engine and a tactical editor.</li>
<li><strong>Kickoff Boss:</strong> a browser manager with multiplayer leagues.</li>
<li><strong>Gaffer's Desk:</strong> a no-download manager inspired by 1990s management games.</li>
<li><strong>Goal Legends:</strong> a browser manager where you start by creating a club.</li>
</ul>`,
      },
      {
        heading: 'Who it is not for',
        html: `<p>If you want licensed real-world player names, a scouting network across 50 countries, and press conferences, a deep paid sim is still the right choice. My Football Game is for managers who want the core loop (squad, tactics, results, pressure) without the download and the time commitment.</p>`,
      },
    ],
    faqs: [
      { q: 'Is there a free game like Football Manager?', a: 'Yes. My Football Game is a free browser football manager with tactics, transfers, training and full seasons with promotion and relegation.' },
      { q: 'Is there a Football Manager alternative with no download?', a: 'Browser managers like My Football Game run in any modern browser, including on Chromebooks and phones, with nothing to install.' },
      { q: 'Does My Football Game use real player names?', a: 'No. Clubs and players are fictional, so every club starts on equal terms.' },
    ],
    related: ['/football-manager-online-free', '/football-manager-no-download', '/soccer-manager-game', '/features/seasons', '/blog/best-free-football-manager-games-online', '/blog/online-soccer-manager-alternative'],
  },
  {
    path: '/press',
    lang: 'en',
    title: 'Press Kit – Facts, Logo and Screenshots | My Football Game',
    description: 'Press kit for My Football Game: a free browser football manager. Short and long descriptions, key facts, logo, screenshots and contact details.',
    eyebrow: 'Press kit',
    h1: 'MY FOOTBALL GAME ',
    h1Accent: 'PRESS KIT',
    subtitle: 'Everything you need to write about the game or list it: descriptions, facts, images and a contact.',
    breadcrumb: 'Press Kit',
    sections: [
      {
        heading: 'Short description',
        html: `<p>My Football Game is a free online football manager that runs in your browser. Build a club, set your tactics and play full seasons with promotion and relegation. No download.</p>`,
      },
      {
        heading: 'Long description',
        html: `<p>My Football Game is a free browser football manager for players who want the core of management (squad, tactics, results, pressure) without a download or a long time commitment. You create your own club, choose from 18 formations and 26 positions, sign players on a shared transfer market and train them individually.</p>
<p>Each season has 18 matchdays against nine rival clubs, with promotion and relegation across five divisions and a board that sets a target and issues ultimatums. Matches are simulated minute by minute with live commentary, possession, shots and xG. A friend can play your club straight from a challenge link, with no signup.</p>`,
      },
      {
        heading: 'Key facts',
        html: `<ul>
<li><strong>Name:</strong> My Football Game</li>
<li><strong>Website:</strong> https://www.myfootballgame.com</li>
<li><strong>Genre:</strong> Football (soccer) management simulation</li>
<li><strong>Platforms:</strong> Any modern browser, on desktop and mobile</li>
<li><strong>Price:</strong> Free to play, with optional gold</li>
<li><strong>Languages:</strong> English, Portuguese and Spanish</li>
<li><strong>Developer:</strong> Paulo Rodrigues, Lisbon, Portugal</li>
</ul>`,
      },
      {
        heading: 'Logo and screenshots',
        html: `<ul>
<li><a href="/logo.png">Logo (PNG)</a></li>
<li><a href="/og-image.jpg">Social sharing image (JPG)</a></li>
<li><a href="/screenshot-season.jpg">Screenshot: season</a></li>
<li><a href="/screenshot-live-match.jpg">Screenshot: live match</a></li>
</ul>
<p>You can use these images when writing about the game. Clubs and players in the game are fictional.</p>`,
      },
      {
        heading: 'Contact',
        html: `<p>For interviews, review access or anything else, email <a href="mailto:olympikesoft@gmail.com">olympikesoft@gmail.com</a>.</p>`,
      },
    ],
    faqs: [
      { q: 'Can I use these images in an article or listing?', a: 'Yes. You can use the logo and screenshots on this page when writing about or listing the game.' },
      { q: 'Is My Football Game free?', a: 'Yes. It is free to play in the browser, with optional gold.' },
    ],
    related: ['/about', '/how-to-play', '/features/seasons', '/games-like-football-manager'],
  },
  {
    path: '/pt/jogo-de-treinador-de-futebol',
    lang: 'pt',
    title: 'Jogo de Treinador de Futebol Online Grátis – Sem Download | My Football Game',
    description: 'Jogo de treinador de futebol online e grátis. Monta o teu plantel, escolhe a tática, contrata jogadores e luta pela subida de divisão. Joga no browser, sem download.',
    eyebrow: 'Grátis · Sem download',
    h1: 'JOGO DE TREINADOR ',
    h1Accent: 'DE FUTEBOL',
    subtitle: 'Um manager de futebol completo que corre no browser. Grátis, sem instalar nada, no computador ou no telemóvel.',
    breadcrumb: 'Jogo de Treinador',
    sections: [
      {
        heading: 'És tu o treinador',
        html: `<p>No My Football Game assumes o comando de um clube. Escolhes o onze inicial, a formação e a mentalidade, defines quem bate os penáltis e os livres, e vês o jogo minuto a minuto com golos, cartões, substituições, posse de bola, remates e xG.</p>`,
      },
      {
        heading: 'Uma época completa em poucas sessões',
        html: `<ul>
<li><strong>18 jornadas</strong> contra nove clubes rivais.</li>
<li><strong>Subida e descida</strong> em cinco divisões: Amadora, Regional, Nacional, Championship e Elite.</li>
<li><strong>Uma direção que te avalia.</strong> Cumpre o objetivo e tens apoio; perde demasiado e recebes um ultimato: ganha 2 dos próximos 3 jogos.</li>
<li><strong>18 formações</strong> e 26 posições, do 4-3-3 ao 3-5-2.</li>
<li><strong>Mercado de transferências</strong> com outros treinadores e treino individual de jogadores.</li>
</ul>`,
      },
      {
        heading: 'Joga contra os teus amigos',
        html: `<p>Envia um link de desafio pelo WhatsApp. O teu amigo joga contra o teu clube logo ali, e cada jogo conta para o vosso histórico de confrontos. Também podes criar uma liga privada e jogar uma época inteira com o teu grupo.</p>`,
      },
      {
        heading: 'Grátis a sério',
        html: `<p>Tudo se ganha a jogar. Há duas moedas: moedas, que ganhas nos jogos e desafios diários, e ouro, que também se pode comprar. O ouro é um atalho, nunca uma barreira.</p>`,
      },
    ],
    faqs: [
      { q: 'O jogo é grátis?', a: 'Sim. É grátis e todas as funcionalidades se ganham a jogar. O ouro é opcional.' },
      { q: 'Preciso de instalar alguma coisa?', a: 'Não. Joga-se no browser, no computador, tablet ou telemóvel.' },
      { q: 'Posso jogar com amigos?', a: 'Sim. Envia um link de desafio ou cria uma liga privada para o teu grupo.' },
    ],
    related: ['/pt', '/football-manager-online-free'],
  },
  {
    path: '/es',
    lang: 'es',
    title: 'Juego de Manager de Fútbol Online Gratis – Sin Descargas | My Football Game',
    description: 'Juego de manager de fútbol online y gratis. Crea tu club, elige la táctica, ficha jugadores y lucha por el ascenso. Se juega en el navegador, sin descargas.',
    eyebrow: 'Gratis · Sin descargas',
    h1: 'MANAGER DE FÚTBOL ',
    h1Accent: 'ONLINE GRATIS',
    subtitle: 'Un manager de fútbol completo que se juega en el navegador. Gratis, sin instalar nada, en el ordenador o en el móvil. Y todo el juego está en español.',
    breadcrumb: 'Manager de fútbol',
    sections: [
      {
        heading: 'Tú eres el entrenador',
        html: `<p>En My Football Game diriges tu propio club. Eliges el once inicial, la formación y la mentalidad, decides quién tira los penaltis y las faltas, y ves el partido minuto a minuto con goles, tarjetas, cambios, posesión, tiros y xG.</p>`,
      },
      {
        heading: 'Una temporada completa en pocas sesiones',
        html: `<ul>
<li><strong>18 jornadas</strong> contra nueve clubes rivales.</li>
<li><strong>Ascensos y descensos</strong> en cinco divisiones: Amateur, Regional, Nacional, Championship y Élite.</li>
<li><strong>Una directiva que te juzga.</strong> Cumple el objetivo y te respalda; pierde demasiado y te da un ultimátum: gana 2 de los próximos 3.</li>
<li><strong>18 formaciones</strong> y 26 posiciones, del 4-3-3 al 3-5-2.</li>
<li><strong>Mercado de fichajes</strong> con otros entrenadores y entrenamiento individual de jugadores.</li>
</ul>`,
      },
      {
        heading: 'Juega contra tus amigos',
        html: `<p>Envía un enlace de desafío por WhatsApp. Tu amigo juega contra tu club al instante, sin registrarse, y cada partido cuenta para vuestro historial cara a cara. También puedes crear una liga privada y jugar una temporada entera con tu grupo.</p>`,
      },
      {
        heading: 'Gratis de verdad',
        html: `<p>Todo se gana jugando. Hay dos monedas: monedas, que ganas en los partidos y desafíos diarios, y oro, que también se puede comprar. El oro es un atajo, nunca una barrera.</p>`,
      },
    ],
    faqs: [
      { q: '¿El juego es gratis?', a: 'Sí. Es gratis y todas las funciones se ganan jugando. El oro es opcional.' },
      { q: '¿Tengo que instalar algo?', a: 'No. Se juega en el navegador, en el ordenador, la tablet o el móvil.' },
      { q: '¿El juego está en español?', a: 'Sí. Todo el juego está traducido al español, y también se puede jugar en inglés y portugués.' },
      { q: '¿Puedo jugar con amigos?', a: 'Sí. Envía un enlace de desafío o crea una liga privada para tu grupo.' },
    ],
    related: ['/es/crear-tu-propio-club-de-futbol', '/es/juego-de-futbol-con-amigos', '/es/juegos-como-football-manager', '/formations'],
  },
  {
    path: '/es/crear-tu-propio-club-de-futbol',
    lang: 'es',
    title: 'Crea tu Propio Club de Fútbol Online Gratis | My Football Game',
    description: 'Crea tu propio club de fútbol desde cero: ponle nombre, sube tu escudo, ficha a tu plantilla y sube de la liga Amateur a la Élite. Gratis, en el navegador y en español.',
    eyebrow: 'Tu club, desde cero',
    h1: 'CREA TU PROPIO ',
    h1Accent: 'CLUB DE FÚTBOL',
    subtitle: 'Tu nombre, tu escudo, tu plantilla. Empieza en la liga Amateur y construye un club que llegue hasta lo más alto.',
    breadcrumb: 'Crea tu club',
    sections: [
      {
        heading: 'Ponle nombre, súbele un escudo y hazlo tuyo',
        html: `<p>En My Football Game no te haces cargo de un club famoso: creas el tuyo. Eliges el nombre del club, subes tu propio escudo (o pegas el enlace de una imagen) y tu club recibe sus colores, generados a partir del nombre, para que se vea igual en todas partes: clasificaciones, pantallas de partido y enlaces de desafío.</p>`,
      },
      {
        heading: 'Construye la plantilla desde la base',
        html: `<p>Tu club empieza con una plantilla y un presupuesto de fichajes. A partir de ahí, cada jugador es decisión tuya:</p>
<ul>
<li><strong>Ficha y vende</strong> en un mercado compartido con otros entrenadores.</li>
<li><strong>Entrena</strong> atributos individuales. Los jóvenes progresan más rápido y los veteranos aportan regularidad.</li>
<li><strong>Elige el sistema</strong> entre 18 formaciones y 26 posiciones, y coloca a cada jugador donde rinde mejor.</li>
<li><strong>Define la táctica:</strong> la mentalidad, la presión y los lanzadores de balón parado cambian cada partido.</li>
</ul>`,
      },
      {
        heading: 'De la liga Amateur a la Élite',
        html: `<p>Todos los clubes empiezan en la división Amateur. Cada temporada tiene 18 jornadas contra nueve clubes rivales, con ascensos y descensos en cinco divisiones: Amateur, Regional, Nacional, Championship y Élite.</p>
<p>La directiva fija un objetivo cada temporada. Si lo cumples, el club crece; si fallas demasiado, recibes un ultimátum: gana 2 de los próximos 3 partidos.</p>`,
      },
      {
        heading: 'Enfrenta tu club al de tus amigos',
        html: `<p>Envía un enlace de desafío y tu amigo juega contra tu club al instante. Cada resultado cuenta para vuestro historial cara a cara. ¿Quieres una competición completa? Crea una liga privada y juega una temporada entera con tu grupo.</p>`,
      },
    ],
    faqs: [
      { q: '¿Puedo crear mi propio club de fútbol online gratis?', a: 'Sí. En My Football Game creas tu club gratis, eliges el nombre, subes un escudo y construyes la plantilla tú mismo.' },
      { q: '¿Puedo elegir el nombre de mi equipo?', a: 'Sí. Le pones nombre al crear el club, y puedes cambiar el nombre y el escudo más adelante en los ajustes del equipo.' },
      { q: '¿Puedo subir mi propio escudo?', a: 'Sí. Puedes subir una imagen o pegar un enlace. Los colores del club se generan automáticamente a partir del nombre.' },
      { q: '¿Los clubes y jugadores son reales?', a: 'No. Los clubes y jugadores son ficticios, así que todos los entrenadores empiezan desde el mismo punto.' },
    ],
    related: ['/es', '/es/juego-de-futbol-con-amigos', '/es/juegos-como-football-manager'],
  },
  {
    path: '/es/juego-de-futbol-con-amigos',
    lang: 'es',
    title: 'Juego de Manager de Fútbol con Amigos – Gratis | My Football Game',
    description: 'Juega al manager de fútbol contra tus amigos: arma tu plantilla, elige la táctica y rétalos con un enlace o en una liga privada. Gratis, en el navegador y sin descargas.',
    eyebrow: 'Entrenador contra entrenador',
    h1: 'MANAGER DE FÚTBOL ',
    h1Accent: 'CON AMIGOS',
    subtitle: 'Arma tu plantilla, elige tu táctica y enfréntala a la de tus amigos. Cada resultado queda en vuestro historial cara a cara.',
    breadcrumb: 'Jugar con amigos',
    sections: [
      {
        heading: 'Tu plantilla contra la suya',
        html: `<p>My Football Game es un manager de fútbol en el que juegas contra personas reales, no solo contra la máquina. Construyes tu plantilla, eliges la formación y la táctica, y el motor simula el partido minuto a minuto a partir de las decisiones de ambos entrenadores. Las estadísticas (posesión, tiros y xG) te muestran por qué ganaste o perdiste.</p>`,
      },
      {
        heading: 'Tres formas de jugar con tus amigos',
        html: `<ul>
<li><strong>Enlace de desafío.</strong> Envía un enlace por WhatsApp, Discord o donde quieras. Tu amigo juega contra tu club al instante, sin registrarse.</li>
<li><strong>Liga privada.</strong> Invita a tu grupo y jugad una temporada entera, con clasificación y premio final.</li>
<li><strong>Lista de amigos.</strong> Añade amigos en el juego y envíales desafíos directos cuando quieras la revancha.</li>
</ul>
<p>Cada partido entre vosotros cuenta para el historial cara a cara, así que las rivalidades crecen temporada tras temporada.</p>`,
      },
      {
        heading: '¿Sin amigos conectados?',
        html: `<p>Busca rivales aleatorios, completa los tres desafíos diarios para ganar monedas extra o sube en la clasificación global contra entrenadores de todo el mundo.</p>`,
      },
    ],
    faqs: [
      { q: '¿Puedo jugar a un manager de fútbol contra mis amigos?', a: 'Sí. Envía un enlace de desafío para un partido o crea una liga privada para jugar una temporada entera.' },
      { q: '¿Mi amigo necesita una cuenta?', a: 'No. Quien abre tu enlace de desafío puede jugar contra tu club sin registrarse.' },
      { q: '¿Se controla a los jugadores en el campo?', a: 'No. Ambos entrenadores eligen plantilla y táctica, y el motor simula el partido minuto a minuto a partir de esas decisiones.' },
      { q: '¿Es gratis?', a: 'Sí. Se juega gratis en el navegador, sin descargas y sin muros de pago.' },
    ],
    related: ['/es', '/es/crear-tu-propio-club-de-futbol', '/es/juegos-como-football-manager'],
  },
  {
    path: '/es/juegos-como-football-manager',
    lang: 'es',
    title: 'Juegos como Football Manager Gratis en el Navegador | My Football Game',
    description: '¿Buscas juegos como Football Manager gratis y sin descargas? Una mirada honesta a las alternativas en navegador y móvil, y para quién es cada una.',
    eyebrow: 'Alternativas',
    h1: 'JUEGOS COMO ',
    h1Accent: 'FOOTBALL MANAGER',
    subtitle: '¿Te gustan los juegos de gestión, pero no el precio, la descarga o las temporadas de decenas de horas? Estas son las alternativas y dónde encaja My Football Game.',
    breadcrumb: 'Juegos como Football Manager',
    sections: [
      {
        heading: 'Qué busca quien quiere una alternativa',
        html: `<p>Football Manager es el simulador de gestión de fútbol más profundo que existe, pero es de pago, ocupa mucho y una temporada puede llevar decenas de horas. Muchos jugadores buscan algo <strong>gratis</strong>, que funcione <strong>en el navegador o en el móvil</strong>, o con <strong>temporadas más cortas</strong> y rivales reales.</p>`,
      },
      {
        heading: 'Los principales tipos de alternativas',
        html: `<ul>
<li><strong>Simuladores profundos para PC.</strong> Juegos de pago con enormes bases de datos. Lo mejor si quieres el máximo detalle y tienes tiempo.</li>
<li><strong>Managers gratuitos para móvil</strong>, como Top Eleven. Apps pulidas pensadas para sesiones diarias y eventos en directo.</li>
<li><strong>Managers clásicos de navegador</strong>, como Hattrick u Online Soccer Manager. Comunidades veteranas, normalmente con calendarios lentos en los que el partido de liga se juega un día fijo.</li>
<li><strong>Managers rápidos de navegador</strong>, como My Football Game. Gratis, sin descargas, y juegas cuando quieres en lugar de esperar a la siguiente jornada.</li>
</ul>`,
      },
      {
        heading: 'Dónde encaja My Football Game',
        html: `<ul>
<li><strong>Juegas a tu ritmo.</strong> Una temporada de 18 jornadas se termina en pocas sesiones.</li>
<li><strong>Tus decisiones importan.</strong> 18 formaciones, 26 posiciones, mentalidad, presión y lanzadores de balón parado influyen en el motor de partidos.</li>
<li><strong>Hay presión.</strong> La directiva fija un objetivo cada temporada y lanza ultimátums si no lo cumples.</li>
<li><strong>Tus amigos juegan en segundos.</strong> Con un enlace de desafío, un amigo juega contra tu club al momento.</li>
<li><strong>Todo el juego está en español</strong>, y funciona en ordenador y móvil.</li>
</ul>`,
      },
      {
        heading: 'Otros managers gratis para navegador',
        html: `<p>Si quieres comparar, estos son otros managers de fútbol que se presentan como gratuitos y para navegador. Consulta las condiciones actuales en cada sitio.</p>
<ul>
<li><strong>Hattrick:</strong> un manager online veterano con una gran comunidad y extras premium opcionales.</li>
<li><strong>ManagerZone:</strong> un manager gratuito para navegador y móvil.</li>
<li><strong>Sokker Manager:</strong> un manager de navegador con motor de partidos 2D y 3D y editor táctico.</li>
<li><strong>Kickoff Boss:</strong> un manager de navegador con ligas multijugador.</li>
<li><strong>Gaffer's Desk:</strong> un manager sin descargas inspirado en los juegos de gestión de los años 90.</li>
<li><strong>Goal Legends:</strong> un manager de navegador en el que empiezas creando un club.</li>
</ul>`,
      },
      {
        heading: 'Para quién no es',
        html: `<p>Si quieres jugadores reales con licencia, una red de ojeadores por todo el mundo y ruedas de prensa, un simulador profundo de pago sigue siendo la mejor opción. My Football Game es para quien quiere lo esencial (plantilla, táctica, resultados y presión) sin descargas y sin tanto tiempo.</p>`,
      },
    ],
    faqs: [
      { q: '¿Hay algún juego gratis como Football Manager?', a: 'Sí. My Football Game es un manager de fútbol gratis para navegador, con tácticas, fichajes, entrenamiento y temporadas completas con ascensos y descensos.' },
      { q: '¿Hay una alternativa a Football Manager sin descargas?', a: 'Sí. Los managers de navegador como My Football Game funcionan en cualquier navegador moderno, también en el móvil, sin instalar nada.' },
      { q: '¿My Football Game usa jugadores reales?', a: 'No. Los clubes y jugadores son ficticios, así que todos los clubes empiezan en igualdad de condiciones.' },
    ],
    related: ['/es', '/es/crear-tu-propio-club-de-futbol', '/es/juego-de-futbol-con-amigos'],
  },
];

export default landingPages;
